/**
 * Shopify data layer — calls the MatryxLab server proxy.
 * The server holds the Admin API token; the browser never sees it.
 */

// The storefront is served by the same Express server that hosts the API, so
// production uses a relative path (same origin, no CORS). VITE_API_URL is only
// needed when the SPA is served from somewhere else (e.g. a separate dev host).
const API_BASE = (import.meta.env.VITE_API_URL || '') + '/api/storefront'

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || `API error ${res.status}`)
  }
  return res.json()
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ShopifyImage {
  src: string
  alt?: string
}

export interface ShopifyVariant {
  id: number
  title: string
  price: string
  available?: boolean
  inventory_quantity?: number
  option1?: string
  option2?: string
  option3?: string
}

export interface ShopifyProduct {
  id: number
  title: string
  handle: string
  body_html: string
  product_type: string
  tags: string | string[]
  images: ShopifyImage[]
  variants: ShopifyVariant[]
  options?: Array<{ name: string; values: string[] }>
}

export interface ShopifyCollection {
  id: number
  title: string
  handle: string
  body_html?: string
  image?: ShopifyImage | null
  type?: string
}

export interface CartLineItem {
  id: number
  variantId: number
  productId: number
  title: string
  variantTitle: string
  quantity: number
  price: string
  image?: string | null
}

export interface Cart {
  id: number
  lineItems: CartLineItem[]
  subtotalPrice: string
  totalPrice: string
  currencyCode: string
}

// ─── Collections ─────────────────────────────────────────────────────────────

export async function fetchCollections(): Promise<ShopifyCollection[]> {
  const data = await apiFetch<{ collections: ShopifyCollection[] }>('/collections')
  return data.collections
}

export async function fetchCollection(handle: string): Promise<{ collection: ShopifyCollection; products: ShopifyProduct[] }> {
  return apiFetch(`/collections/${handle}`)
}

// ─── Products ─────────────────────────────────────────────────────────────────

export async function fetchProducts(limit = 20): Promise<ShopifyProduct[]> {
  const data = await apiFetch<{ products: ShopifyProduct[] }>(`/products?limit=${limit}`)
  return data.products
}

export async function fetchProduct(handle: string): Promise<ShopifyProduct> {
  const data = await apiFetch<{ product: ShopifyProduct }>(`/products/${handle}`)
  return data.product
}

// ─── Cart ─────────────────────────────────────────────────────────────────────

export async function createCart(items: Array<{ variantId: number; quantity: number }>): Promise<Cart> {
  const data = await apiFetch<{ cart: Cart }>('/cart', {
    method: 'POST',
    body: JSON.stringify({ items }),
  })
  return data.cart
}

export async function updateCart(cartId: number, items: Array<{ variantId: number; quantity: number }>): Promise<Cart> {
  const data = await apiFetch<{ cart: Cart }>(`/cart/${cartId}`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  })
  return data.cart
}

export async function fetchCart(cartId: number): Promise<Cart> {
  const data = await apiFetch<{ cart: Cart }>(`/cart/${cartId}`)
  return data.cart
}

export async function getCheckoutUrl(cartId: number): Promise<string> {
  const data = await apiFetch<{ checkoutUrl: string }>(`/cart/${cartId}/checkout`, { method: 'POST' })
  return data.checkoutUrl
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getProductImage(product: ShopifyProduct): string {
  return product.images?.[0]?.src || '/placeholder-product.jpg'
}

export function formatPrice(price: string | number, currency = 'CAD'): string {
  const amount = typeof price === 'string' ? parseFloat(price) : price
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount)
}

export function getProductTags(product: ShopifyProduct): string[] {
  if (!product.tags) return []
  if (Array.isArray(product.tags)) return product.tags
  return (product.tags as string).split(',').map((t: string) => t.trim()).filter(Boolean)
}
